import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const StageConfigPanel = ({ stage, onUpdate, onDelete, onClose }) => {
  const [editedStage, setEditedStage] = useState(stage);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = () => {
    onUpdate(stage?.id, editedStage);
  };

  const handleDelete = () => {
    onDelete(stage?.id);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="bg-card rounded-xl p-4 sm:p-6 border border-border sticky top-24">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Stage Configuration</h2>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      <div className="space-y-4">
        {/* Stage Name */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Stage Name
          </label>
          <Input
            value={editedStage?.stageName}
            onChange={(e) => setEditedStage({ ...editedStage, stageName: e?.target?.value })}
          />
        </div>

        {/* Stage Order */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Order Position
          </label>
          <Input
            type="number"
            value={editedStage?.stageOrder}
            onChange={(e) => setEditedStage({ ...editedStage, stageOrder: parseInt(e?.target?.value) })}
          />
        </div>

        {/* Color */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={editedStage?.color}
              onChange={(e) => setEditedStage({ ...editedStage, color: e?.target?.value })}
              className="w-12 h-10 rounded border border-border cursor-pointer"
            />
            <Input
              value={editedStage?.color}
              onChange={(e) => setEditedStage({ ...editedStage, color: e?.target?.value })}
              className="flex-1"
            />
          </div>
        </div>

        {/* Icon */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Icon
          </label>
          <select
            value={editedStage?.icon}
            onChange={(e) => setEditedStage({ ...editedStage, icon: e?.target?.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary"
          >
            {['Circle', 'UserPlus', 'Phone', 'CheckCircle', 'Calendar', 'FileText', 'MessageSquare', 'DollarSign', 'Trophy', 'Star']?.map((icon) => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>

        {/* Active Status */}
        <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
          <div>
            <p className="text-sm font-medium text-foreground">Active Status</p>
            <p className="text-xs text-muted-foreground">Enable or disable this stage</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={editedStage?.isActive}
              onChange={(e) => setEditedStage({ ...editedStage, isActive: e?.target?.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        {/* Approval Required */}
        <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
          <div>
            <p className="text-sm font-medium text-foreground">Approval Required</p>
            <p className="text-xs text-muted-foreground">Require approval to move to this stage</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={editedStage?.approvalRequired}
              onChange={(e) => setEditedStage({ ...editedStage, approvalRequired: e?.target?.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 space-y-2">
          <Button onClick={handleSave} fullWidth iconName="Save">
            Save Changes
          </Button>
          <Button
            variant="outline"
            fullWidth
            iconName="Trash2"
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
          >
            Delete Stage
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl p-6 max-w-md w-full border border-border">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mr-4">
                <Icon name="AlertTriangle" size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Stage?</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete the stage "{stage?.stageName}"? All leads in this stage will need to be reassigned.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageConfigPanel;